import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Filter as FilterIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import type { Dataset, Datapoint, Filter } from "@/types/dataset";
import { DatasetList } from "@/components/datasets/DatasetList";
import { DatapointList } from "@/components/datasets/DatapointList";
import { FilterList } from "@/components/datasets/FilterList";
import { DatasetForm } from "@/components/datasets/forms/DatasetForm";
import { DatapointForm } from "@/components/datasets/forms/DatapointForm";
import { FilterForm } from "@/components/datasets/forms/FilterForm";
import { SmartTagForm } from "@/components/datasets/forms/SmartTagForm";

const Datasets = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [selectedDatapoint, setSelectedDatapoint] = useState<Datapoint | null>(null);
  const [isDatasetDialogOpen, setIsDatasetDialogOpen] = useState(false);
  const [isDatapointDialogOpen, setIsDatapointDialogOpen] = useState(false);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isSmartTagDialogOpen, setIsSmartTagDialogOpen] = useState(false);
  const [editingDataset, setEditingDataset] = useState<Dataset | null>(null);
  const [editingDatapoint, setEditingDatapoint] = useState<Datapoint | null>(null);
  const [editingFilter, setEditingFilter] = useState<Filter | null>(null);

  const { data: datasets, isLoading: isLoadingDatasets } = useQuery({
    queryKey: ["datasets"],
    queryFn: async () => {
      if (!session) throw new Error("No session");

      const { data, error } = await supabase.from("datasets").select("*").order("name", { ascending: true });

      if (error) throw error;
      return data as Dataset[];
    },
    enabled: !!session,
  });

  const { data: datapoints } = useQuery({
    queryKey: ["datapoints", selectedDataset?.id],
    queryFn: async () => {
      if (!selectedDataset) return [];

      const { data, error } = await supabase
        .from("datapoints")
        .select(
          `
          *,
          smart_tags (
            id,
            datapoint_id,
            tag_value,
            created_at,
            updated_at
          )
        `,
        )
        .eq("dataset_id", selectedDataset.id)
        .order("name", { ascending: true }); // Explicitly order by name alphabetically

      if (error) throw error;
      return data as Datapoint[];
    },
    enabled: !!selectedDataset,
  });

  const { data: filters } = useQuery({
    queryKey: ["filters", selectedDataset?.id],
    queryFn: async () => {
      if (!selectedDataset) return [];

      const { data, error } = await supabase
        .from("filters")
        .select("*")
        .eq("dataset_id", selectedDataset.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!selectedDataset,
  });

  const updateDatasetMutation = useMutation({
    mutationFn: async (values: { id: string; name: string; description?: string }) => {
      if (!values.name) throw new Error("Name is required");

      const { data, error } = await supabase
        .from("datasets")
        .update({
          name: values.name,
          description: values.description,
        })
        .eq("id", values.id)
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
      setEditingDataset(null);
      setIsDatasetDialogOpen(false);
      toast.success("Dataset updated successfully");
    },
  });

  const updateDatapointMutation = useMutation({
    mutationFn: async (values: { id: string; name: string }) => {
      if (!values.name) throw new Error("Name is required");

      const { data, error } = await supabase
        .from("datapoints")
        .update({ name: values.name })
        .eq("id", values.id)
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datapoints", selectedDataset?.id],
      });
      setEditingDatapoint(null);
      setIsDatapointDialogOpen(false);
      toast.success("Datapoint updated successfully");
    },
  });

  const updateFilterMutation = useMutation({
    mutationFn: async (values: { id: string; name: string; datapoints: string[] }) => {
      if (!values.name) throw new Error("Name is required");
      if (!values.datapoints?.length) throw new Error("At least one datapoint is required");

      const { data: filterData, error: filterError } = await supabase
        .from("filters")
        .update({ name: values.name })
        .eq("id", values.id)
        .select();

      if (filterError) throw filterError;

      // Update filter_datapoints
      const { error: deleteError } = await supabase.from("filter_datapoints").delete().eq("filter_id", values.id);

      if (deleteError) throw deleteError;

      const { error: linkError } = await supabase.from("filter_datapoints").insert(
        values.datapoints.map((datapointId) => ({
          filter_id: values.id,
          datapoint_id: datapointId,
        })),
      );

      if (linkError) throw linkError;

      return filterData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["filters", selectedDataset?.id],
      });
      setEditingFilter(null);
      setIsFilterDialogOpen(false);
      toast.success("Filter updated successfully");
    },
  });

  const deleteFilterMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("filters").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["filters", selectedDataset?.id],
      });
      toast.success("Filter deleted successfully");
    },
  });

  const createDatasetMutation = useMutation({
    mutationFn: async (values: { name: string; description?: string }) => {
      if (!values.name) throw new Error("Name is required");

      const { data, error } = await supabase.from("datasets").insert([values]).select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
      setIsDatasetDialogOpen(false);
      toast.success("Dataset created successfully");
    },
  });

  const createDatapointMutation = useMutation({
    mutationFn: async (values: { name: string }) => {
      if (!values.name) throw new Error("Name is required");
      if (!selectedDataset) throw new Error("No dataset selected");

      const { data, error } = await supabase
        .from("datapoints")
        .insert([
          {
            name: values.name,
            dataset_id: selectedDataset.id,
          },
        ])
        .select();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datapoints", selectedDataset?.id],
      });
      setIsDatapointDialogOpen(false);
      toast.success("Datapoint created successfully");
    },
  });

  const createFilterMutation = useMutation({
    mutationFn: async (values: { name: string; datapoints: string[] }) => {
      if (!values.name) throw new Error("Name is required");
      if (!values.datapoints?.length) throw new Error("At least one datapoint is required");
      if (!selectedDataset) throw new Error("No dataset selected");

      const { data: filterData, error: filterError } = await supabase
        .from("filters")
        .insert([
          {
            name: values.name,
            dataset_id: selectedDataset.id,
          },
        ])
        .select()
        .single();

      if (filterError) throw filterError;

      if (values.datapoints.length > 0) {
        const { error: linkError } = await supabase.from("filter_datapoints").insert(
          values.datapoints.map((datapointId) => ({
            filter_id: filterData.id,
            datapoint_id: datapointId,
          })),
        );

        if (linkError) throw linkError;
      }

      return filterData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["filters", selectedDataset?.id],
      });
      setIsFilterDialogOpen(false);
      toast.success("Filter created successfully");
    },
  });

  const deleteDatasetMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("datasets").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
      setSelectedDataset(null);
      toast.success("Dataset deleted successfully");
    },
  });

  const deleteDatapointMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("datapoints").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datapoints", selectedDataset?.id],
      });
      toast.success("Datapoint deleted successfully");
    },
  });

  const createSmartTagMutation = useMutation({
    mutationFn: async (values: { tag_value: string }) => {
      if (!selectedDatapoint) throw new Error("No datapoint selected");

      const { data, error } = await supabase
        .from("smart_tags")
        .insert([
          {
            datapoint_id: selectedDatapoint.id,
            tag_value: values.tag_value,
          },
        ])
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datapoints", selectedDataset?.id],
      });
      setIsSmartTagDialogOpen(false);
      toast.success("Smart tag added successfully");
    },
  });

  const deleteSmartTagMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("smart_tags").delete().eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["datapoints", selectedDataset?.id],
      });
      toast.success("Smart tag deleted successfully");
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Data Management</h1>
          <p className="text-muted-foreground">Create and manage datasets and datapoints for your templates.</p>
        </div>
        <Dialog
          open={isDatasetDialogOpen}
          onOpenChange={(open) => {
            setIsDatasetDialogOpen(open);
            if (!open) {
              setEditingDataset(null);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2" />
              New Dataset
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingDataset ? "Edit Dataset" : "Create New Dataset"}</DialogTitle>
            </DialogHeader>
            <DatasetForm
              initialData={editingDataset || undefined}
              onSubmit={(values) => {
                if (!values.name) return;
                if (editingDataset) {
                  updateDatasetMutation.mutate({
                    id: editingDataset.id,
                    name: values.name,
                    description: values.description,
                  });
                } else {
                  createDatasetMutation.mutate({
                    name: values.name,
                    description: values.description,
                  });
                }
              }}
            />
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Datasets</h2>
        <DatasetList
          datasets={datasets}
          onSelectDataset={setSelectedDataset}
          onDeleteDataset={(id) => deleteDatasetMutation.mutate(id)}
          onEditDataset={(dataset) => {
            setEditingDataset(dataset);
            setIsDatasetDialogOpen(true);
          }}
        />
      </div>

      {selectedDataset && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Datapoints for {selectedDataset.name}</h2>
            <div className="flex gap-2">
              <Dialog
                open={isDatapointDialogOpen}
                onOpenChange={(open) => {
                  setIsDatapointDialogOpen(open);
                  if (!open) {
                    setEditingDatapoint(null);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2" />
                    Add Datapoint
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingDatapoint ? "Edit Datapoint" : "Add New Datapoint"}</DialogTitle>
                  </DialogHeader>
                  <DatapointForm
                    initialData={editingDatapoint || undefined}
                    onSubmit={(values) => {
                      if (!values.name) return;
                      if (editingDatapoint) {
                        updateDatapointMutation.mutate({
                          id: editingDatapoint.id,
                          name: values.name,
                        });
                      } else {
                        createDatapointMutation.mutate({
                          name: values.name,
                        });
                      }
                    }}
                  />
                </DialogContent>
              </Dialog>

              <Dialog
                open={isFilterDialogOpen}
                onOpenChange={(open) => {
                  setIsFilterDialogOpen(open);
                  if (!open) {
                    setEditingFilter(null);
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button>
                    <FilterIcon className="mr-2" />
                    Create Filter
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingFilter ? "Edit Filter" : "Create New Filter"}</DialogTitle>
                  </DialogHeader>
                  <FilterForm
                    datapoints={datapoints}
                    initialData={editingFilter || undefined}
                    onSubmit={(values) => {
                      if (!values.name || !values.datapoints?.length) return;
                      if (editingFilter) {
                        updateFilterMutation.mutate({
                          id: editingFilter.id,
                          name: values.name,
                          datapoints: values.datapoints,
                        });
                      } else {
                        createFilterMutation.mutate({
                          name: values.name,
                          datapoints: values.datapoints,
                        });
                      }
                    }}
                  />
                </DialogContent>
              </Dialog>
            </div>
          </div>

          <div className="space-y-4">
            <DatapointList
              datapoints={datapoints}
              onDeleteDatapoint={(id) => deleteDatapointMutation.mutate(id)}
              onEditDatapoint={(datapoint) => {
                setEditingDatapoint(datapoint);
                setIsDatapointDialogOpen(true);
              }}
              onAddSmartTag={(datapoint) => {
                setSelectedDatapoint(datapoint);
                setIsSmartTagDialogOpen(true);
              }}
              onDeleteSmartTag={(id) => deleteSmartTagMutation.mutate(id)}
            />
          </div>

          <Dialog open={isSmartTagDialogOpen} onOpenChange={setIsSmartTagDialogOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Smart Tag</DialogTitle>
              </DialogHeader>
              <SmartTagForm onSubmit={createSmartTagMutation.mutate} />
            </DialogContent>
          </Dialog>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Filters</h2>
            <FilterList
              filters={filters}
              onDeleteFilter={(id) => deleteFilterMutation.mutate(id)}
              onEditFilter={(filter) => {
                setEditingFilter(filter);
                setIsFilterDialogOpen(true);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Datasets;
